import config from '../../../../../config';

import { FC, Fragment, useState } from 'react';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { exportInvoicesToPDF } from 'app/store/BillingInvoices/BillingInvoices.async';
// Actions
import { BillingInvoicesActions } from 'app/store/BillingInvoices/BillingInvoices.slice';
// Selectors
import { selectBatchInvoicesAmount, selectBatchInvoicesIds, selectHasSentBatchInvoices, selectParams, selectInvoicesTotal } from 'app/store/BillingInvoices/BillingInvoices.selectors';
// Mui
import { Box, Tooltip, Typography } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
// Components
import { Button, IconButton } from 'app/components/Mui';
// Utilities
import { toNumberWithCommas } from 'app/utilities/Utilities';
// 
import BillingCheckFormDialog from './ACHPaymentFormDialog';
import BillingCardFormDialog from './CreditCardPaymentFormDialog';
import UpcomingChargeInfo from './UpcomingChargeInfo';
// i18next
import { useTranslation } from 'react-i18next';
import RequestInterimInvoiceButton from '../RequestInterimInvoiceButton';
// Mixpanel
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';

const BillingInvoicesBatchPayment:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const batchInvoicesIds = useAppSelector(selectBatchInvoicesIds);
  const batchInvoicesAmount = useAppSelector(selectBatchInvoicesAmount);
  const hasSentBatchInvoices = useAppSelector(selectHasSentBatchInvoices);
  const params = useAppSelector(selectParams);
  const invoicesTotal = useAppSelector(selectInvoicesTotal);

  const [ dialogCheckOpen, setDialogCheckOpen ] = useState<boolean>(false);
  const [ dialogCardOpen, setDialogCardOpen ] = useState<boolean>(false);

  const [ loading, setLoading ] = useState(false);

  const toggleDialogCheck = () => setDialogCheckOpen((prevState:boolean) => !prevState);
  const toggleDialogCard = () => setDialogCardOpen((prevState:boolean) => !prevState);

  const handleExportInvoicesToPDF = async () => {
    setLoading(true);
    try {
      await dispatch(exportInvoicesToPDF(batchInvoicesIds)).unwrap();
      dispatch(MixpanelActions.handleBillingInvoicesBulkPDFExportClick({ invoicesIds: batchInvoicesIds }));
      dispatch(BillingInvoicesActions.setInitialField('batchInvoices'));
    } catch(error){} finally {
      setLoading(false);
    }
  }

  const handleClearAll = () => {
    dispatch(BillingInvoicesActions.setInitialField('batchInvoices'));
  }

  return (
    <Fragment>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 4
      }}>
        {batchInvoicesIds.length ? (
          <Tooltip title={t('buttons.clearAll')}>
            <IconButton
              name="Clear all"
              onClick={handleClearAll}
              size="small"
            ><ClearIcon fontSize="small" /></IconButton>
          </Tooltip>
        ) : null}
        {batchInvoicesAmount ? (
          <Typography sx={{ display: 'block' }} variant="subtitle2">
            {`
              ${t('pages.billingAndSubscriptions.batchInvoicesSummary', { invoicesCount: batchInvoicesIds.length, invoicesTotal })}
              ${batchInvoicesAmount.outstanding ? ` ${t('labels.outstanding')} $${toNumberWithCommas(batchInvoicesAmount.outstanding)}.` : ''}
              ${batchInvoicesAmount.paid ? ` ${t('labels.paid')} $${toNumberWithCommas(batchInvoicesAmount.paid)}.` : ''}
            `}
          </Typography>
        ) : <UpcomingChargeInfo />}
        <Box sx={{ flexGrow: 1 }} />
        {config.domain === '.com' ? (
          <Button
            name="Bulk ACH payment"
            onClick={toggleDialogCheck}
            variant="contained"
            size="small"
            disabled={!batchInvoicesIds.length || !hasSentBatchInvoices}
          >{t('pages.billingAndSubscriptions.achPaymentButton')}</Button>
        ) : null}
        <Button
          name="Bulk pay with credit card"
          onClick={toggleDialogCard}
          variant="contained"
          size="small"
          disabled={!batchInvoicesIds.length || !hasSentBatchInvoices}
        >{t('pages.billingAndSubscriptions.creditCardPaymentButton')}</Button>
        <Button
          name="Bulk export invoices to PDF"
          loading={loading}
          onClick={handleExportInvoicesToPDF}
          variant="contained"
          color="primary"
          size="small"
          disabled={!batchInvoicesIds.length}
        >{t('pages.billingAndSubscriptions.exportToPdfButton')}</Button>
        <RequestInterimInvoiceButton predefinedCase={params['case']} />
      </Box>
      {dialogCheckOpen ? (
        <BillingCheckFormDialog
          open={dialogCheckOpen}
          onClose={toggleDialogCheck}
        />
      ) : null}
      {dialogCardOpen ? (
        <BillingCardFormDialog
          open={dialogCardOpen}
          onClose={toggleDialogCard}
        />
      ) : null}
    </Fragment>
  )
}

export default BillingInvoicesBatchPayment;
